import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./EventManagement.css";
import Header from "../components/header/Header";
import StatCards from "../components/statCard/StatCard";
import TabSelector from "../components/tabSelector/TabSelector";
import EventCard from "../components/eventCard/EventCard";
import { CalendarIcon, PlusCircleIcon } from "../helper/icon";
import { AuthContext } from "../context/auth";
import { formatDateWithOrdinal } from "../helper/utils";
import NoEventDisplay from "../components/noEventDisplay/NoEventDisplay";
import { sortEventsByDate } from "../helper/utils";
import EventCardSkeleton from "../components/skeletons/EventCardSkeletons";

const EventManagement = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [guestFilter, setGuestFilter] = useState("all");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState({
    upcomingEvents: [],
    activeEvents: [],
    pastEvents: [],
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const userId = user?._id;
  const userRole = user?.activeRole || "";

  useEffect(() => {
    const loadEvents = async () => {
      if (!userId) {
        setError("Please log in to view events.");
        setLoading(false);
        return;
      }
      setLoading(true);
      const data = await fetchEvents(userId);
      setEvents(data);
      computeStats(data);
      setLoading(false);
    };
    loadEvents();
  }, [userId]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    const dateUpdateInterval = setInterval(() => {
      setCurrentDate(new Date());
    }, 60000);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearInterval(dateUpdateInterval);
    };
  }, []);

  const fetchEvents = async (hostId) => {
    try {
      const response = await axios.get(`/events/host/${hostId}`, {});
      if (response.data.successStatus) {
        const upcomingEvents = sortEventsByDate(
          response.data.upcomingEvents || []
        );
        const activeEvents = sortEventsByDate(response.data.activeEvents || []);
        const pastEvents = sortEventsByDate(response.data.pastEvents || []);

        return {
          upcomingEvents,
          activeEvents,
          pastEvents,
        };
      } else {
        // throw new Error("Failed to fetch events");
      }
    } catch (error) {
      setError("Failed to load events. Please try again.");
      return { upcomingEvents: [], activeEvents: [], pastEvents: [] };
    }
  };

  const computeStats = (eventsData) => {
    const allEvents = [
      ...eventsData.upcomingEvents,
      ...eventsData.activeEvents,
      ...eventsData.pastEvents,
    ];

    const computedStats = {
      totalEvents: allEvents.length,
      totalGuests: allEvents.reduce(
        (sum, event) => sum + (event.invitations?.length || 0),
        0
      ),
      overallAmount: allEvents.reduce(
        (sum, event) =>
          sum +
          (event.invitations?.reduce(
            (invSum, inv) => invSum + (inv.totalPaid || 0),
            0
          ) || 0),

        0
      ),

      eventAttireAmount: allEvents.reduce(
        (sum, event) =>
          sum +
          (event.invitations?.reduce(
            (invSum, inv) =>
              invSum +
              (inv.eventAttire
                ?.filter((attire) => attire.paid)
                ?.reduce((acc, attire) => acc + (attire.totalAmount || 0), 0) ||
                0),
            0
          ) || 0),

        0
      ),

      ticketAmount: allEvents.reduce(
        (sum, event) =>
          sum +
          (event.invitations?.reduce(
            (invSum, inv) =>
              invSum +
              (inv.ticketFeePaid?.reduce(
                (acc, ticket) => acc + (ticket.price || 0),
                0
              ) || 0),
            0
          ) || 0),
        0
      ),

      cashGiftAmount: allEvents.reduce(
        (sum, event) =>
          sum +
          (event.invitations?.reduce(
            (invSum, inv) => invSum + (inv.cashGiftPaid?.giftAmountPaid || 0),
            0
          ) || 0),
        0
      ),
      refundRequests: allEvents.reduce(
        (sum, event) =>
          sum +
          (event.invitations?.filter((inv) => inv.refundStatus === "requested")
            ?.length || 0),
        0
      ),
    };

    // console.log("Computed stats in management:", computedStats);
    setStats(computedStats);
  };

  const getEventStatus = (eventDate) => {
    if (!eventDate) return "Upcoming";
    const today = new Date();
    const event = new Date(eventDate);
    const todayDateOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const eventDateOnly = new Date(
      event.getFullYear(),
      event.getMonth(),
      event.getDate()
    );

    if (eventDateOnly > todayDateOnly) {
      return "Upcoming";
    } else if (eventDateOnly.getTime() === todayDateOnly.getTime()) {
      return "Live";
    } else {
      return "Complete";
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleEventClick = (event) => {
    // console.log("navigating with Event ID:", event);
    navigate(`/eventManagementDetails/${event.id || event._id}`, {
      state: {
        eventTitle: event.title || event.eventName,
        eventDate: event.eventDate || event.startDate,
        eventStatus: getEventStatus(event.eventDate || event.startDate),
        eventDetails: event,
        from: "/eventManagement",
      },
    });
  };

  const displayedEvents =
    activeTab === "upcoming"
      ? events.upcomingEvents
      : activeTab === "active"
      ? events.activeEvents
      : events.pastEvents;

  const handleCreateEvent = () => {
    navigate("/createEvent");
  };
  return (
    <div className="event-management">
      <Header />

      <div className="event-management-header">
        <div className="event-management-header-title">
          <h1>Event Management</h1>
        </div>

        <div className="event-management-header-actions">
          <div className="event-management-date-card">
            <div className="event-management-calendar-icon">
              <CalendarIcon />
            </div>
            <div className="event-management-date-info">
              <p>Today's Date</p>
              <h3>{formatDateWithOrdinal(currentDate)}</h3>
            </div>
          </div>

          <button
            className="event-management-create-event-btn"
            onClick={handleCreateEvent}
          >
            <span className="event-management-plus-icon">
              <PlusCircleIcon />
            </span>
            Create Event
          </button>
        </div>
      </div>

      <div className="event-management-dashboard">
        {!loading &&
        displayedEvents.length === 0 &&
        activeTab === "upcoming" ? (
          <NoEventDisplay imageType={isMobile ? "mobile" : "desktop"} />
        ) : (
          <div>
            <div className="event-management-stats-grid">
              {/* <StatCards
                stats={stats}
                displayCards={{
                  totalEvent: true,
                  eventAttire: true,
                  ticketAmount: true,
                  cashGift: true,
                  totalGuest: true,
                  overallAmount: false,
                  refundRequest: false,
                }}
              /> */}
            </div>

            <div className="event-management-events-section">
              <TabSelector
                tabs={[
                  { id: "upcoming", label: "Upcoming Event" },
                  { id: "active", label: "Active" },
                  { id: "history", label: "History" },
                ]}
                onTabChange={handleTabChange}
                withSearch={true}
                badgeCounts={{
                  upcoming: events.upcomingEvents.length,
                  active: events.activeEvents?.length || 0,
                  history: events.pastEvents.length,
                }}
              />

              <div className="event-management-events-list">
                {loading ? (
                  <>
                    {Array.from({ length: isMobile ? 4 : 6 }).map(
                      (_, index) => (
                        <EventCardSkeleton key={index} />
                      )
                    )}
                  </>
                ) : error && error === "404" ? (
                  <div>
                    {error}
                    <button
                      onClick={() => {
                        setError(null);
                        setLoading(true);
                        fetchEvents(userId).then((data) => {
                          setEvents(data);
                          computeStats(data);
                          setLoading(false);
                        });
                      }}
                      style={{ marginLeft: "10px", padding: "5px 10px" }}
                    >
                      Retry
                    </button>
                  </div>
                ) : displayedEvents.length === 0 ? (
                  <div>
                    No{" "}
                    {activeTab === "upcoming"
                      ? "upcoming"
                      : activeTab === "active"
                      ? "active"
                      : "past"}{" "}
                    events found.
                  </div>
                ) : (
                  displayedEvents.map((event) => (
                    <EventCard
                      key={event._id || event.id}
                      date={formatDateWithOrdinal(
                        event.eventDate ||
                          event.startDate ||
                          (event.multipleDates && event.multipleDates[0]?.date)
                      )}
                      title={event.eventTitle || "Untitled Event"}
                      type={event.eventType || "Unknown"}
                      time={
                        event.startTime ||
                        event.eventTime ||
                        new Date(
                          event.eventDate || event.startDate
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      }
                      location={
                        event.eventLandmark ||
                        event.eventLocation ||
                        event.customAddress ||
                        "Not specified"
                      }
                      image={
                        event.eventImages?.length > 0 &&
                        event.eventImages[0]?.uri
                          ? event.eventImages[0].uri
                          : "/images/eventImage.jpg"
                      }
                      invitations={event.invitations || []}
                      onPress={() => handleEventClick(event)}
                      isUsher={userRole === "usher" ? true : false}
                      checkedInCount={event.checkedInCount || 0}
                      totalGuests={event.invitations?.length || 0}
                      approvalStatus={event.approvalStatus || null}
                      event={event}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventManagement;
